import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Plus, Copy, Link2, Users, TrendingUp, Loader2, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';

interface AffiliateStats {
  affiliate_id: string;
  affiliate_code: string;
  affiliate_name: string;
  owner_id: string;
  owner_email: string;
  active: boolean;
  total_referrals: number;
  seller_referrals: number;
  created_at: string;
}

interface UserOption {
  id: string;
  email: string;
  full_name: string | null;
}

export default function AffiliateManagement() {
  const { toast } = useToast();
  const [affiliates, setAffiliates] = useState<AffiliateStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searching, setSearching] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [linkName, setLinkName] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  useEffect(() => {
    loadAffiliates();
  }, []);

  const loadAffiliates = async () => {
    try {
      const { data, error } = await supabase.rpc('get_affiliate_stats');
      if (error) throw error;
      setAffiliates(data || []);
    } catch (error: any) {
      toast({
        title: 'Error loading affiliates',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!searchEmail.trim()) return;
    setSearching(true);
    
    try {
      // Search for users by name in profiles first
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, full_name, username')
        .or(`full_name.ilike.%${searchEmail}%,username.ilike.%${searchEmail}%`)
        .limit(20);
      
      if (error) throw error;
      
      // Get emails for matched profiles
      const usersWithEmail: UserOption[] = [];
      for (const profile of profiles || []) {
        try {
          const { data: email } = await supabase.rpc('admin_get_user_email', { p_user_id: profile.id });
          if (email) {
            usersWithEmail.push({
              id: profile.id,
              email: email,
              full_name: profile.full_name || profile.username,
            });
          }
        } catch (e) {
          // Skip if email fetch fails
          console.log('Could not fetch email for user', profile.id);
        }
      }
      
      // If no name matches, try searching by email pattern
      if (usersWithEmail.length === 0 && searchEmail.includes('@')) {
        const { data: allProfiles } = await supabase
          .from('profiles')
          .select('id, full_name, username')
          .limit(50);
        
        for (const profile of allProfiles || []) {
          try {
            const { data: email } = await supabase.rpc('admin_get_user_email', { p_user_id: profile.id });
            if (email && email.toLowerCase().includes(searchEmail.toLowerCase())) {
              usersWithEmail.push({
                id: profile.id,
                email: email,
                full_name: profile.full_name || profile.username,
              });
            }
          } catch (e) {
            // Skip
          }
        }
      }
      
      setUsers(usersWithEmail);
      
      if (usersWithEmail.length === 0) {
        toast({
          title: 'No users found',
          description: 'Try searching by name or email',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error searching users:', error);
      toast({
        title: 'Error searching users',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSearching(false);
    }
  };

  const generateCode = async (): Promise<string> => {
    // Generate a random 8-character code if RPC fails
    const { data, error } = await supabase.rpc('generate_affiliate_code');
    if (error) {
      console.error('RPC generate_affiliate_code failed:', error);
      // Fallback: generate code locally
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      let code = '';
      for (let i = 0; i < 8; i++) {
        code += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return code;
    }
    return data;
  };

  const createAffiliateLink = async () => {
    if (!selectedUserId) {
      toast({
        title: 'Select a user',
        description: 'Please search and select a user to create an affiliate link for.',
        variant: 'destructive',
      });
      return;
    }

    setCreating(true);
    try {
      const code = await generateCode();
      
      const { error } = await supabase
        .from('affiliate_links')
        .insert({
          user_id: selectedUserId,
          code,
          name: linkName || null,
        });

      if (error) throw error;

      toast({
        title: 'Affiliate link created',
        description: `Link with code ${code} has been created.`,
      });

      setDialogOpen(false);
      setSelectedUserId('');
      setLinkName('');
      setSearchEmail('');
      setUsers([]);
      loadAffiliates();
    } catch (error: any) {
      toast({
        title: 'Error creating link',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (affiliateId: string, currentActive: boolean) => {
    try {
      const { error } = await supabase
        .from('affiliate_links')
        .update({ active: !currentActive })
        .eq('id', affiliateId);

      if (error) throw error;

      setAffiliates(prev =>
        prev.map(a =>
          a.affiliate_id === affiliateId ? { ...a, active: !currentActive } : a
        )
      );

      toast({
        title: currentActive ? 'Link deactivated' : 'Link activated',
      });
    } catch (error: any) {
      toast({
        title: 'Error updating link',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const copyLink = (code: string) => {
    const link = `${window.location.origin}/auth?ref=${code}`;
    navigator.clipboard.writeText(link);
    toast({
      title: 'Link copied!',
      description: 'Affiliate signup link copied to clipboard.',
    });
  };

  const totalReferrals = affiliates.reduce((sum, a) => sum + Number(a.total_referrals), 0);
  const totalSellers = affiliates.reduce((sum, a) => sum + Number(a.seller_referrals), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Links</CardTitle>
            <Link2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{affiliates.filter(a => a.active).length}</div>
            <p className="text-xs text-muted-foreground">
              of {affiliates.length} total links
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Referrals</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalReferrals}</div>
            <p className="text-xs text-muted-foreground">
              users signed up via affiliate links
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Seller Conversions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalSellers}</div>
            <p className="text-xs text-muted-foreground">
              referrals became sellers
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Sales Affiliate Links</CardTitle>
            <CardDescription>
              Create and manage unique signup links for your sales team
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create Link
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Affiliate Link</DialogTitle>
                <DialogDescription>
                  Assign a unique affiliate signup link to a team member
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Search User by Name or Email</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter name or email..."
                        value={searchEmail}
                        onChange={(e) => setSearchEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                      />
                      <Button type="button" variant="secondary" onClick={searchUsers} disabled={searching}>
                        {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
                      </Button>
                    </div>
                  </div>

                {users.length > 0 && (
                  <div className="space-y-2">
                    <Label>Select User</Label>
                    <div className="max-h-40 overflow-y-auto space-y-2 rounded-md border p-2">
                      {users.map((user) => (
                        <div
                          key={user.id}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                            selectedUserId === user.id
                              ? 'bg-primary/10 border border-primary'
                              : 'hover:bg-muted'
                          }`}
                          onClick={() => setSelectedUserId(user.id)}
                        >
                          <div>
                            <p className="font-medium text-sm">{user.full_name || 'No name'}</p>
                            <p className="text-xs text-muted-foreground">{user.email}</p>
                          </div>
                          {selectedUserId === user.id && (
                            <Badge variant="default">Selected</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="linkName">Link Name (Optional)</Label>
                  <Input
                    id="linkName"
                    placeholder="e.g., John's Link, Lagos Team"
                    value={linkName}
                    onChange={(e) => setLinkName(e.target.value)}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={createAffiliateLink} disabled={creating || !selectedUserId}>
                  {creating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Link'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {affiliates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Link2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No affiliate links created yet</p>
              <p className="text-sm">Create your first affiliate link to start tracking referrals</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name / Code</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead className="text-center">Referrals</TableHead>
                  <TableHead className="text-center">Sellers</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {affiliates.map((affiliate) => (
                  <TableRow key={affiliate.affiliate_id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{affiliate.affiliate_name || 'Unnamed'}</p>
                        <code className="text-xs bg-muted px-1 py-0.5 rounded">
                          {affiliate.affiliate_code}
                        </code>
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-sm">{affiliate.owner_email}</p>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{affiliate.total_referrals}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="default">{affiliate.seller_referrals}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(affiliate.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={affiliate.active}
                        onCheckedChange={() => toggleActive(affiliate.affiliate_id, affiliate.active)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyLink(affiliate.affiliate_code)}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copy
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(`/auth?ref=${affiliate.affiliate_code}`, '_blank')}
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
